import React, { useEffect, useMemo, useRef, useState } from "react";
import { CFormTextarea } from "@coreui/react-pro";
import config from "../../config/config";
import publicRoadmapContent from "../../content/publicRoadmapContent";
import {
  createAudioLevelMonitor,
  fileExtensionFromMimeType,
  pickRecordingMimeType,
  requestAudioStream,
  stopMediaStream,
} from "../../utils/browserAudio";

const GUIDE_NAME = "experts4europe-guide";
const PLAYBACK_GAIN_BOOST = 2.2;

const speechLocaleMap = {
  en: "en-US",
  de: "de-DE",
  es: "es-ES",
  fr: "fr-FR",
  ar: "ar-SA"
};

const mouthShapes = {
  idle: {
    outer: "M79 131 Q100 137 121 131",
    inner: "",
    shadow: "M82 131 Q100 135 118 131",
  },
  listening: [
    {
      outer: "M78 130 Q100 139 122 130",
      inner: "M86 131 Q100 137 114 131 Q112 137 100 139 Q88 137 86 131 Z",
      shadow: "M85 132 Q100 137 115 132",
    },
    {
      outer: "M77 129 Q100 142 123 129",
      inner: "M84 130 Q100 141 116 130 Q114 142 100 145 Q86 142 84 130 Z",
      shadow: "M84 131 Q100 141 116 131",
    },
    {
      outer: "M78 128 Q100 140 122 128",
      inner: "M90 126 Q100 132 110 126 Q113 142 100 146 Q87 142 90 126 Z",
      shadow: "M88 130 Q100 139 112 130",
    },
  ],
  speaking: [
    {
      outer: "M77 129 Q100 141 123 129",
      inner: "M86 130 Q100 140 114 130 Q112 140 100 143 Q88 140 86 130 Z",
      shadow: "M84 131 Q100 139 116 131",
    },
    {
      outer: "M76 127 Q100 147 124 127",
      inner: "M84 128 Q100 145 116 128 Q114 148 100 152 Q86 148 84 128 Z",
      shadow: "M84 130 Q100 144 116 130",
    },
    {
      outer: "M81 124 Q100 138 119 124 Q122 145 100 150 Q78 145 81 124 Z",
      inner: "M87 125 Q100 136 113 125 Q114 143 100 147 Q86 143 87 125 Z",
      shadow: "M88 128 Q100 138 112 128",
    },
    {
      outer: "M74 129 Q100 149 126 129",
      inner: "M82 129 Q100 148 118 129 Q116 151 100 156 Q84 151 82 129 Z",
      shadow: "M83 131 Q100 146 117 131",
    },
    {
      outer: "M82 123 Q100 130 118 123 Q121 145 100 150 Q79 145 82 123 Z",
      inner: "M90 123 Q100 128 110 123 Q112 141 100 145 Q88 141 90 123 Z",
      shadow: "M90 127 Q100 133 110 127",
    },
  ],
};

const guideConsoleCopy = {
  en: { console: "Public guide console", status: "Guide status", language: "Language context", transcript: "Live transcript", ready: "Ready", listening: "Listening", thinking: "Thinking", speaking: "Speaking", liveCapture: "Live speech capture", recordedCapture: "Recorded voice fallback", renderingAudio: "Preparing the human voice reply.", idleHint: "experts4europe-guide is ready for the next public question.", listeningHint: "Speak clearly into your microphone. Stop capture when your question is complete.", recordingHint: "Voice capture is recording from your microphone and can be sent when you stop.", transcribingHint: "Voice capture is being transcribed for the public guide.", thinkingHint: "experts4europe-guide is preparing a public answer.", speakingHint: "The human voice reply is currently playing through your audio output.", micStart: "Start recording", micStop: "Stop recording and send", permissionPrompt: "Microphone permission is required before live listening can start.", permissionDenied: "Microphone access was blocked. Please allow it in your browser settings.", unsupportedMic: "This browser does not support microphone capture for the public guide.", unsupportedAudio: "Human voice output is not available right now.", audioInteractionRequired: "Tap the guide waveform once to enable browser audio output.", noSpeech: "No speech was detected. Please try again.", micMissing: "No microphone was found. Please check your headset or browser input device.", voiceError: "Voice interaction is temporarily unavailable.", transcribeError: "Voice capture could not be transcribed right now.", permissionState: "Mic permission" },
  de: { console: "Public-Guide-Konsole", status: "Guide-Status", language: "Sprachkontext", transcript: "Live-Transkript", ready: "Bereit", listening: "Hört zu", thinking: "Denkt nach", speaking: "Spricht", liveCapture: "Live-Spracherfassung", recordedCapture: "Aufnahme-Fallback", renderingAudio: "Die menschliche Sprachantwort wird vorbereitet.", idleHint: "experts4europe-guide ist bereit für die nächste öffentliche Frage.", listeningHint: "Sprechen Sie klar in Ihr Mikrofon. Beenden Sie die Aufnahme, wenn die Frage vollständig ist.", recordingHint: "Die Sprachaufnahme läuft über Ihr Mikrofon und wird nach dem Beenden übergeben.", transcribingHint: "Die Sprachaufnahme wird für den öffentlichen Guide transkribiert.", thinkingHint: "experts4europe-guide bereitet eine öffentliche Antwort vor.", speakingHint: "Die menschliche Sprachantwort läuft gerade über Ihre Audioausgabe.", micStart: "Aufnahme starten", micStop: "Aufnahme beenden und senden", permissionPrompt: "Für Live-Listening wird zuerst die Mikrofonfreigabe benötigt.", permissionDenied: "Der Mikrofonzugriff wurde blockiert. Bitte erlauben Sie das Mikrofon in Ihren Browser-Einstellungen.", unsupportedMic: "Dieser Browser unterstützt keine Mikrofonerfassung für den öffentlichen Guide.", unsupportedAudio: "Die menschliche Sprachausgabe ist gerade nicht verfügbar.", audioInteractionRequired: "Tippen Sie einmal auf die Wellenfläche des Guides, um die Browser-Audioausgabe zu aktivieren.", noSpeech: "Es wurde keine Sprache erkannt. Bitte versuchen Sie es noch einmal.", micMissing: "Kein Mikrofon gefunden. Bitte prüfen Sie Headset oder Browser-Eingang.", voiceError: "Die Voice-Interaktion ist momentan nicht verfügbar.", transcribeError: "Die Sprachaufnahme konnte gerade nicht transkribiert werden.", permissionState: "Mikrofonstatus" },
  es: { console: "Consola publica del guide", status: "Estado del guide", language: "Contexto de idioma", transcript: "Transcripcion en vivo", ready: "Lista", listening: "Escuchando", thinking: "Procesando", speaking: "Hablando", liveCapture: "Captura de voz en vivo", recordedCapture: "Respaldo por grabacion", renderingAudio: "Se esta preparando la respuesta con voz humana.", idleHint: "experts4europe-guide esta listo para la siguiente pregunta publica.", listeningHint: "Hable con claridad al microfono. Detenga la captura cuando termine su pregunta.", recordingHint: "La voz se esta grabando desde el microfono y se enviara al detenerla.", transcribingHint: "La captura de voz se esta transcribiendo para el guide publico.", thinkingHint: "experts4europe-guide esta preparando una respuesta publica.", speakingHint: "La respuesta con voz humana se esta reproduciendo por la salida de audio.", micStart: "Iniciar grabacion", micStop: "Detener grabacion y enviar", permissionPrompt: "Se necesita permiso del microfono antes de iniciar la escucha en vivo.", permissionDenied: "El acceso al microfono fue bloqueado. Permitelo en la configuracion del navegador.", unsupportedMic: "Este navegador no admite captura de microfono para el guide publico.", unsupportedAudio: "La salida con voz humana no esta disponible ahora mismo.", audioInteractionRequired: "Toque una vez la onda del guide para habilitar el audio del navegador.", noSpeech: "No se detecto voz. Intentelo de nuevo.", micMissing: "No se encontro microfono. Revise el headset o la entrada del navegador.", voiceError: "La interaccion por voz no esta disponible temporalmente.", transcribeError: "La captura de voz no pudo transcribirse en este momento.", permissionState: "Permiso del microfono" },
  fr: { console: "Console publique du guide", status: "Statut du guide", language: "Contexte de langue", transcript: "Transcription en direct", ready: "Pret", listening: "Ecoute", thinking: "Reflexion", speaking: "Parle", liveCapture: "Capture vocale en direct", recordedCapture: "Secours par enregistrement", renderingAudio: "La reponse a voix humaine est en cours de preparation.", idleHint: "experts4europe-guide est pret pour la prochaine question publique.", listeningHint: "Parlez clairement dans votre microphone. Arretez la capture quand votre question est complete.", recordingHint: "La voix est en cours d'enregistrement depuis le microphone et sera envoyee a l'arret.", transcribingHint: "La capture vocale est en cours de transcription pour le guide public.", thinkingHint: "experts4europe-guide prepare une reponse publique.", speakingHint: "La reponse a voix humaine est en cours de lecture via votre sortie audio.", micStart: "Demarrer l'enregistrement", micStop: "Arreter l'enregistrement et envoyer", permissionPrompt: "L'autorisation du microphone est necessaire avant l'ecoute en direct.", permissionDenied: "L'acces au microphone a ete bloque. Autorisez-le dans les reglages du navigateur.", unsupportedMic: "Ce navigateur ne prend pas en charge la capture micro pour le guide public.", unsupportedAudio: "La sortie a voix humaine n'est pas disponible pour le moment.", audioInteractionRequired: "Touchez une fois la forme d'onde du guide pour activer l'audio du navigateur.", noSpeech: "Aucune voix detectee. Veuillez reessayer.", micMissing: "Aucun microphone detecte. Verifiez le casque ou l'entree du navigateur.", voiceError: "L'interaction vocale est temporairement indisponible.", transcribeError: "La capture vocale n'a pas pu etre transcrite pour le moment.", permissionState: "Autorisation micro" },
  ar: { console: "وحدة الدليل العامة", status: "حالة الدليل", language: "سياق اللغة", transcript: "النص الحي", ready: "جاهز", listening: "يستمع", thinking: "يعالج", speaking: "يتحدث", liveCapture: "التقاط صوت مباشر", recordedCapture: "مسار تسجيل احتياطي", renderingAudio: "يتم تجهيز الرد بصوت بشري.", idleHint: "experts4europe-guide جاهز للسؤال العام التالي.", listeningHint: "تحدث بوضوح في الميكروفون. اوقف الالتقاط عندما تنتهي من السؤال.", recordingHint: "يتم تسجيل الصوت من الميكروفون وسيتم ارساله عند الايقاف.", transcribingHint: "يتم تفريغ التسجيل الصوتي للدليل العام.", thinkingHint: "يقوم experts4europe-guide باعداد اجابة عامة.", speakingHint: "يتم تشغيل الرد البشري الان عبر خرج الصوت.", micStart: "بدء التسجيل", micStop: "ايقاف التسجيل وارسال", permissionPrompt: "يلزم اذن الميكروفون قبل بدء الاستماع المباشر.", permissionDenied: "تم حظر الوصول الى الميكروفون. يرجى السماح به من اعدادات المتصفح.", unsupportedMic: "هذا المتصفح لا يدعم التقاط الميكروفون للدليل العام.", unsupportedAudio: "الاخراج بصوت بشري غير متاح حاليا.", audioInteractionRequired: "اضغط مرة واحدة على موجة الدليل لتفعيل صوت المتصفح.", noSpeech: "لم يتم التقاط صوت. حاول مرة اخرى.", micMissing: "لم يتم العثور على ميكروفون. تحقق من السماعة او ادخال المتصفح.", voiceError: "التفاعل الصوتي غير متاح مؤقتا.", transcribeError: "تعذر تفريغ التسجيل الصوتي حاليا.", permissionState: "اذن الميكروفون" }
};

const guideSurfaceCopy = {
  en: {
    guideLabel: "Public roadmap guide",
    title: "A curated guide to the public roadmap",
    askLabel: "Public question",
    askHint: "Ask a concrete question about the current phase, the pilot, status labels or the next rollout step.",
    submitLabel: "Ask guide",
    quickPromptsLabel: "Quick starts",
    currentPhaseLabel: "Current phase",
    pilotLabel: "Pilot context",
    statusLegendLabel: "How to read the status",
    topicsLabel: "Ask about",
    responseTitle: "Public answer",
    responseLead: "Questions and answers stay grounded in the public roadmap surface.",
    questionLabel: "Question",
    answerLabel: "Guide answer",
    scopeLabel: "Scope",
    voiceLabel: "Interaction",
    phaseUnknown: "Public phase available",
    voiceModes: { ready: "Text first, voice optional", listening: "Listening is active", thinking: "Preparing answer", speaking: "Playing voice reply" },
    statusDescriptions: {
      in_progress: "This phase is actively being built or operationally prepared.",
      planned: "This phase is sequenced next, but not yet running publicly.",
      completed: "This part is already delivered or publicly in place."
    }
  },
  de: {
    guideLabel: "Öffentlicher Roadmap-Guide",
    title: "Kuratiert durch die öffentliche Roadmap",
    askLabel: "Öffentliche Frage",
    askHint: "Fragen Sie gezielt nach aktueller Phase, Pilot, Statusbezeichnungen oder dem nächsten Rollout-Schritt.",
    submitLabel: "Guide fragen",
    quickPromptsLabel: "Schnelle Einstiege",
    currentPhaseLabel: "Aktuelle Phase",
    pilotLabel: "Pilot-Kontext",
    statusLegendLabel: "So lesen Sie den Status",
    topicsLabel: "Fragen Sie nach",
    responseTitle: "Öffentliche Antwort",
    responseLead: "Die Antworten bleiben an den öffentlichen Roadmap-Inhalten dieser Seite orientiert.",
    questionLabel: "Frage",
    answerLabel: "Guide-Antwort",
    scopeLabel: "Wissensscope",
    voiceLabel: "Interaktion",
    phaseUnknown: "Öffentliche Phase verfügbar",
    voiceModes: { ready: "Text zuerst, Stimme optional", listening: "Mikrofon ist aktiv", thinking: "Antwort wird vorbereitet", speaking: "Sprachantwort läuft" },
    statusDescriptions: {
      in_progress: "Diese Phase ist aktuell in Arbeit oder wird operativ vorbereitet.",
      planned: "Diese Phase ist als nächster Schritt vorgesehen, aber noch nicht öffentlich aktiv.",
      completed: "Dieser Abschnitt ist bereits umgesetzt oder öffentlich eingeführt."
    }
  },
  es: {
    guideLabel: "Guide publico de la roadmap",
    title: "Orientacion curada para la roadmap publica",
    askLabel: "Pregunta publica",
    askHint: "Pregunte por la fase actual, el piloto, las etiquetas de estado o el siguiente paso del rollout.",
    submitLabel: "Preguntar",
    quickPromptsLabel: "Inicios rapidos",
    currentPhaseLabel: "Fase actual",
    pilotLabel: "Contexto del piloto",
    statusLegendLabel: "Como leer el estado",
    topicsLabel: "Pregunte por",
    responseTitle: "Respuesta publica",
    responseLead: "Las respuestas se mantienen dentro de la hoja de ruta publica.",
    questionLabel: "Pregunta",
    answerLabel: "Respuesta del guide",
    scopeLabel: "Alcance",
    voiceLabel: "Interaccion",
    phaseUnknown: "Fase publica disponible",
    voiceModes: { ready: "Texto primero, voz opcional", listening: "Escucha activa", thinking: "Preparando respuesta", speaking: "Reproduciendo voz" },
    statusDescriptions: {
      in_progress: "Esta fase se esta construyendo o preparando de forma operativa.",
      planned: "Esta fase es la siguiente en la secuencia, pero aun no esta activa publicamente.",
      completed: "Esta parte ya esta entregada o visible publicamente."
    }
  },
  fr: {
    guideLabel: "Guide public de la roadmap",
    title: "Un guidage public et structure de la roadmap",
    askLabel: "Question publique",
    askHint: "Posez une question precise sur la phase actuelle, le pilote, les statuts ou la prochaine etape.",
    submitLabel: "Questionner",
    quickPromptsLabel: "Entrees rapides",
    currentPhaseLabel: "Phase actuelle",
    pilotLabel: "Contexte du pilote",
    statusLegendLabel: "Lire les statuts",
    topicsLabel: "Demandez a propos de",
    responseTitle: "Reponse publique",
    responseLead: "Les reponses restent ancrees dans la roadmap publique.",
    questionLabel: "Question",
    answerLabel: "Reponse du guide",
    scopeLabel: "Perimetre",
    voiceLabel: "Interaction",
    phaseUnknown: "Phase publique disponible",
    voiceModes: { ready: "Texte d'abord, voix optionnelle", listening: "Ecoute active", thinking: "Preparation de la reponse", speaking: "Lecture vocale" },
    statusDescriptions: {
      in_progress: "Cette phase est en cours de construction ou de preparation operative.",
      planned: "Cette phase est prevue ensuite, sans etre encore active publiquement.",
      completed: "Cette partie est deja livree ou visible publiquement."
    }
  },
  ar: {
    guideLabel: "دليل خارطة الطريق العام",
    title: "ارشاد منظم لقراءة خارطة الطريق العامة",
    askLabel: "سؤال عام",
    askHint: "اسأل عن المرحلة الحالية او المشروع التجريبي او معاني الحالات او الخطوة التالية.",
    submitLabel: "اسأل الدليل",
    quickPromptsLabel: "بدايات سريعة",
    currentPhaseLabel: "المرحلة الحالية",
    pilotLabel: "سياق المشروع التجريبي",
    statusLegendLabel: "كيف تقرأ الحالة",
    topicsLabel: "اسأل عن",
    responseTitle: "اجابة عامة",
    responseLead: "تبقى الاجابات ضمن محتوى خارطة الطريق العامة.",
    questionLabel: "السؤال",
    answerLabel: "اجابة الدليل",
    scopeLabel: "النطاق",
    voiceLabel: "التفاعل",
    phaseUnknown: "مرحلة عامة متاحة",
    voiceModes: { ready: "النص اولا والصوت اختياري", listening: "الاستماع نشط", thinking: "يتم تحضير الاجابة", speaking: "يتم تشغيل الرد الصوتي" },
    statusDescriptions: {
      in_progress: "هذه المرحلة قيد البناء او التحضير التشغيلي حاليا.",
      planned: "هذه المرحلة هي الخطوة التالية لكنها ليست نشطة علنا بعد.",
      completed: "هذا الجزء منجز او ظاهر علنا بالفعل."
    }
  }
};

function getLanguageMeta(languages, code) {
  return languages.find((entry) => entry.code === code) || languages[0];
}

function createMessage(role, text) {
  return { id: `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, role, text: String(text || "").trim() };
}

function createSilentWavBlob(durationMs = 140) {
  const sampleRate = 8000;
  const channels = 1;
  const bitsPerSample = 16;
  const blockAlign = channels * (bitsPerSample / 8);
  const byteRate = sampleRate * blockAlign;
  const sampleCount = Math.max(1, Math.round((sampleRate * Math.max(durationMs, 80)) / 1000));
  const dataSize = sampleCount * blockAlign;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  function writeString(offset, value) {
    for (let index = 0; index < value.length; index += 1) {
      view.setUint8(offset + index, value.charCodeAt(index));
    }
  }

  writeString(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, channels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);
  writeString(36, "data");
  view.setUint32(40, dataSize, true);

  return new Blob([buffer], { type: "audio/wav" });
}

function waitForAudioReady(audioElement, timeoutMs = 8000) {
  return new Promise((resolve, reject) => {
    if (!audioElement) {
      reject(new Error("audio-unavailable"));
      return;
    }

    if (audioElement.readyState >= 2) {
      resolve();
      return;
    }

    let timeoutId = null;

    const cleanup = () => {
      if (timeoutId) window.clearTimeout(timeoutId);
      audioElement.removeEventListener("loadeddata", handleReady);
      audioElement.removeEventListener("canplay", handleReady);
      audioElement.removeEventListener("error", handleError);
    };

    const handleReady = () => {
      cleanup();
      resolve();
    };

    const handleError = () => {
      cleanup();
      reject(new Error("audio-load-failed"));
    };

    timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error("audio-load-timeout"));
    }, timeoutMs);

    audioElement.addEventListener("loadeddata", handleReady);
    audioElement.addEventListener("canplay", handleReady);
    audioElement.addEventListener("error", handleError);
  });
}

function waitForPlaybackStart(audioElement, timeoutMs = 6000) {
  return new Promise((resolve, reject) => {
    if (!audioElement) {
      reject(new Error("audio-unavailable"));
      return;
    }

    if (!audioElement.paused && !audioElement.ended) {
      resolve();
      return;
    }

    let timeoutId = null;

    const cleanup = () => {
      if (timeoutId) window.clearTimeout(timeoutId);
      audioElement.removeEventListener("playing", handlePlaying);
      audioElement.removeEventListener("error", handleError);
    };

    const handlePlaying = () => {
      cleanup();
      resolve();
    };

    const handleError = () => {
      cleanup();
      reject(new Error("audio-play-failed"));
    };

    timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error("audio-play-timeout"));
    }, timeoutMs);

    audioElement.addEventListener("playing", handlePlaying);
    audioElement.addEventListener("error", handleError);
  });
}

function getSpeechRecognitionCtor() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

function normaliseMessageBlocks(text) {
  const lines = String(text || "").replace(/\r\n/g, "\n").trim().split("\n");
  const blocks = [];
  let paragraph = [];
  let list = null;

  function flushParagraph() {
    if (paragraph.length) {
      blocks.push({ type: "paragraph", text: paragraph.join(" ") });
      paragraph = [];
    }
  }

  function flushList() {
    if (list?.items?.length) blocks.push(list);
    list = null;
  }

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) {
      flushParagraph();
      flushList();
      return;
    }

    const ordered = trimmed.match(/^(\d+)\.\s+(.*)$/);
    const unordered = trimmed.match(/^[-*•]\s+(.*)$/);

    if (ordered) {
      flushParagraph();
      if (!list || list.type !== "ordered") {
        flushList();
        list = { type: "ordered", items: [] };
      }
      list.items.push(ordered[2]);
      return;
    }

    if (unordered) {
      flushParagraph();
      if (!list || list.type !== "unordered") {
        flushList();
        list = { type: "unordered", items: [] };
      }
      list.items.push(unordered[1]);
      return;
    }

    flushList();
    paragraph.push(trimmed);
  });

  flushParagraph();
  flushList();
  return blocks.length ? blocks : [{ type: "paragraph", text: String(text || "").trim() }];
}

function getPupilTransform(state, micLevel) {
  if (state === "thinking") return "translate(-2 -1)";
  if (state === "speaking") return "translate(1 0)";
  if (state === "listening") {
    const shift = Math.min(1.5, Math.max(-1.5, micLevel * 8 - 1.5));
    return `translate(${shift.toFixed(2)} 0)`;
  }
  return "translate(0 0)";
}

function getMouthShape(state, mouthFrame, micLevel) {
  if (state === "speaking") {
    return mouthShapes.speaking[mouthFrame % mouthShapes.speaking.length];
  }
  if (state === "listening") {
    const index = Math.min(mouthShapes.listening.length - 1, Math.floor(Math.max(0, micLevel) * mouthShapes.listening.length * 1.8));
    return mouthShapes.listening[index];
  }
  return mouthShapes.idle;
}

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setPrefersReducedMotion(mediaQuery.matches);
    update();
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", update);
      return () => mediaQuery.removeEventListener("change", update);
    }
    mediaQuery.addListener(update);
    return () => mediaQuery.removeListener(update);
  }, []);

  return prefersReducedMotion;
}

function PublicGuideTalkingHead({
  avatarState,
  mouthFrame,
  micLevel,
  playbackLevel,
  blinkClosed,
  locale,
  voiceCopy,
  isListening,
  onToggleListening,
}) {
  const mouth = getMouthShape(avatarState, mouthFrame, micLevel);
  const pupilTransform = getPupilTransform(avatarState, micLevel);
  const eyePath = blinkClosed ? "M66 95 H84" : "M66 95 Q75 86 84 95";
  const oppositeEyePath = blinkClosed ? "M116 95 H134" : "M116 95 Q125 86 134 95";
  const activeLevel = Math.max(micLevel, playbackLevel);

  return (
    <div className={`public-roadmap-guide__head-stage is-${avatarState}`.trim()}>
      <div className="public-roadmap-guide__head-shell">
        <div className="public-roadmap-guide__head-beacon" aria-hidden="true"><span /><span /><span /></div>
        <svg viewBox="0 0 200 220" className="public-roadmap-guide__head-svg" aria-label={`${GUIDE_NAME} ${voiceCopy[avatarState]}`} role="img">
          <defs>
            <linearGradient id="publicGuideHeadBackdrop" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#f7fafb" />
              <stop offset="100%" stopColor="#dce8ea" />
            </linearGradient>
            <linearGradient id="publicGuideHeadSuit" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#1f3550" />
              <stop offset="100%" stopColor="#345b66" />
            </linearGradient>
            <linearGradient id="publicGuideHeadSkin" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#f0c7b0" />
              <stop offset="100%" stopColor="#d79c7f" />
            </linearGradient>
            <linearGradient id="publicGuideHeadHair" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#2c3d4f" />
              <stop offset="100%" stopColor="#102130" />
            </linearGradient>
          </defs>

          <rect x="12" y="12" width="176" height="196" rx="32" fill="url(#publicGuideHeadBackdrop)" />
          <ellipse cx="100" cy="192" rx="44" ry="10" fill="rgba(25, 50, 74, 0.08)" />
          <g className="public-roadmap-guide__head-group">
            <path d="M56 220 L68 170 C78 159 89 154 100 154 C111 154 122 159 132 170 L144 220 Z" fill="url(#publicGuideHeadSuit)" />
            <path d="M86 149 C88 160 93 169 100 169 C107 169 112 160 114 149 L114 160 C110 166 106 170 100 171 C94 170 90 166 86 160 Z" fill="url(#publicGuideHeadSkin)" />
            <path d="M73 220 L100 175 L127 220 Z" fill="#f5f8f9" opacity="0.95" />
            <ellipse cx="52" cy="94" rx="9" ry="13" fill="url(#publicGuideHeadSkin)" />
            <ellipse cx="148" cy="94" rx="9" ry="13" fill="url(#publicGuideHeadSkin)" />
            <path d="M53 82 C56 45 81 26 100 26 C121 26 145 42 147 83 C141 63 125 52 100 52 C77 52 60 63 53 82 Z" fill="url(#publicGuideHeadHair)" />
            <path d="M60 66 C68 45 82 34 100 34 C118 34 132 45 140 66 L140 120 C136 141 121 155 100 156 C79 155 64 141 60 120 Z" fill="url(#publicGuideHeadSkin)" />
            <path d="M60 87 C63 64 80 48 100 48 C120 48 136 63 140 86 C136 72 123 62 100 62 C78 62 65 72 60 87 Z" fill="url(#publicGuideHeadHair)" />
            <path d="M63 78 C69 58 83 47 100 47 C117 47 131 58 137 78" fill="none" stroke="url(#publicGuideHeadHair)" strokeWidth="10" strokeLinecap="round" />
            <path d="M57 80 C63 42 84 20 100 20 C118 20 139 34 145 78" fill="none" stroke="#24384e" strokeWidth="7" strokeLinecap="round" opacity="0.92" />
            <ellipse cx="48" cy="95" rx="11" ry="17" fill="#203649" />
            <ellipse cx="152" cy="95" rx="11" ry="17" fill="#203649" />
            <ellipse cx="48" cy="95" rx="6" ry="11" fill="#d9e5e7" opacity="0.92" />
            <ellipse cx="152" cy="95" rx="6" ry="11" fill="#d9e5e7" opacity="0.92" />
            <path d="M152 109 C165 114 171 123 170 132 C169 141 161 147 149 148" fill="none" stroke="#203649" strokeWidth="4.5" strokeLinecap="round" />
            <circle cx="146" cy="149" r="6" fill="#203649" />
            <circle cx="146" cy="149" r="2.6" fill="#f4f7f9" opacity="0.92" />
            <path d="M66 86 Q76 80 86 84" fill="none" stroke="#2a3542" strokeWidth="3.6" strokeLinecap="round" />
            <path d="M114 84 Q124 80 134 86" fill="none" stroke="#2a3542" strokeWidth="3.6" strokeLinecap="round" />
            <path d={eyePath} fill="none" stroke="#1d2732" strokeWidth="3.4" strokeLinecap="round" />
            <path d={oppositeEyePath} fill="none" stroke="#1d2732" strokeWidth="3.4" strokeLinecap="round" />
            {!blinkClosed ? (
              <>
                <g transform={pupilTransform}>
                  <circle cx="76" cy="95" r="3.1" fill="#1d2732" />
                  <circle cx="124" cy="95" r="3.1" fill="#1d2732" />
                </g>
                <circle cx="77" cy="94" r="1" fill="#f6f9fa" opacity="0.8" />
                <circle cx="125" cy="94" r="1" fill="#f6f9fa" opacity="0.8" />
              </>
            ) : null}
            <path d="M100 98 L95 118 Q100 121 105 118" fill="none" stroke="#b1705e" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M84 122 Q100 126 116 122" fill="none" stroke="#c28974" strokeWidth="2.4" strokeLinecap="round" opacity="0.72" />
            <path d={mouth.outer} fill="none" stroke="#8c4154" strokeWidth="4.2" strokeLinecap="round" strokeLinejoin="round" />
            {mouth.inner ? <path d={mouth.inner} fill="#72263a" opacity="0.94" /> : null}
            <path d={mouth.shadow} fill="none" stroke="#f4c0b7" strokeWidth="2.1" strokeLinecap="round" opacity="0.65" />
          </g>
        </svg>
      </div>
      <div className="public-roadmap-guide__head-audio">
        <button
          type="button"
          className={`public-roadmap-guide__head-wave ${isListening ? "is-listening" : ""}`.trim()}
          onClick={onToggleListening}
          aria-pressed={isListening}
          aria-label={isListening ? voiceCopy.micStop : voiceCopy.micStart}
        >
          <span style={{ "--public-guide-wave": `${0.22 + activeLevel * 0.9}` }} />
          <span style={{ "--public-guide-wave": `${0.34 + activeLevel * 0.82}` }} />
          <span style={{ "--public-guide-wave": `${0.46 + activeLevel * 0.76}` }} />
          <span style={{ "--public-guide-wave": `${0.38 + activeLevel * 0.84}` }} />
          <span style={{ "--public-guide-wave": `${0.28 + activeLevel * 0.88}` }} />
        </button>
        <span className={`public-roadmap-guide__head-speaker ${isListening ? "is-listening" : ""}`.trim()} aria-hidden="true">
          <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
            <path d="M4 10h4l5-4v12l-5-4H4z" fill="currentColor" />
            <path d="M16 9a4 4 0 010 6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M17.8 6.8a7 7 0 010 10.4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </span>
      </div>
    </div>
  );
}

export default function PublicRoadmapGuide({ language, locale, languages }) {
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [voiceNotice, setVoiceNotice] = useState("");
  const [voiceError, setVoiceError] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState("");
  const [micPermissionState, setMicPermissionState] = useState("prompt");
  const [micLevel, setMicLevel] = useState(0);
  const [playbackLevel, setPlaybackLevel] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speakingMessageId, setSpeakingMessageId] = useState("");
  const [captureMode, setCaptureMode] = useState("ready");
  const [mouthFrame, setMouthFrame] = useState(0);
  const [blinkClosed, setBlinkClosed] = useState(false);
  const [hasRequestedMicrophone, setHasRequestedMicrophone] = useState(false);

  const recognitionRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const meterCleanupRef = useRef(null);
  const interimTranscriptRef = useRef("");
  const recordedChunksRef = useRef([]);
  const submitTranscriptOnEndRef = useRef(true);
  const audioElementRef = useRef(null);
  const playbackUrlRef = useRef("");
  const playbackPrimedRef = useRef(false);
  const playbackAudioContextRef = useRef(null);
  const playbackMediaSourceRef = useRef(null);
  const playbackGainRef = useRef(null);
  const speakingMotionTimerRef = useRef(null);
  const languageMeta = getLanguageMeta(languages, language);
  const voiceCopy = guideConsoleCopy[language] || guideConsoleCopy.en;
  const guideUi = guideSurfaceCopy[language] || guideSurfaceCopy.en;
  const speechRecognitionSupported = Boolean(getSpeechRecognitionCtor());
  const prefersReducedMotion = usePrefersReducedMotion();
  const avatarState = isSpeaking ? "speaking" : loading || isTranscribing ? "thinking" : isListening ? "listening" : "ready";
  const guideIntroParagraphs = String(locale.page.guide.consoleIntro || "")
    .split(/\n\s*\n/g)
    .map((entry) => entry.trim())
    .filter(Boolean);
  const phases = Array.isArray(locale.page?.phases) ? locale.page.phases : [];
  const currentPhaseIndex = Math.max(phases.findIndex((phase) => phase.status === "in_progress"), 0);
  const currentPhase = phases[currentPhaseIndex] || phases[0] || null;
  const nextPhase = phases[currentPhaseIndex + 1] || phases[currentPhaseIndex] || currentPhase;
  const exampleQuestions = Array.isArray(locale.page.guide.exampleQuestions)
    ? locale.page.guide.exampleQuestions.slice(0, 5)
    : [];
  const statusLabels = publicRoadmapContent.statusLabels || {};
  const statusLegend = useMemo(() => ([
    {
      key: "in_progress",
      label: statusLabels.in_progress?.[language] || "In progress",
      body: guideUi.statusDescriptions.in_progress,
    },
    {
      key: "planned",
      label: statusLabels.planned?.[language] || "Planned",
      body: guideUi.statusDescriptions.planned,
    },
    {
      key: "completed",
      label: statusLabels.completed?.[language] || "Completed",
      body: guideUi.statusDescriptions.completed,
    },
  ]), [guideUi.statusDescriptions.completed, guideUi.statusDescriptions.in_progress, guideUi.statusDescriptions.planned, language, statusLabels.completed, statusLabels.in_progress, statusLabels.planned]);
  const voiceMode = avatarState === "listening"
    ? guideUi.voiceModes.listening
    : avatarState === "thinking"
      ? guideUi.voiceModes.thinking
      : avatarState === "speaking"
        ? guideUi.voiceModes.speaking
        : guideUi.voiceModes.ready;
  const guideDisplayName = guideUi.guideLabel;
  const curatedCards = [
    {
      key: "phase",
      eyebrow: guideUi.currentPhaseLabel,
      title: currentPhase?.title || guideUi.phaseUnknown,
      body: locale.publicAnswers?.current_stage || currentPhase?.summary || locale.page.guide.body,
    },
    {
      key: "pilot",
      eyebrow: guideUi.pilotLabel,
      title: locale.page.pilot?.title || guideUi.pilotLabel,
      body: locale.publicAnswers?.pilot || locale.page.pilot?.body || locale.page.guide.body,
    },
    {
      key: "topics",
      eyebrow: guideUi.topicsLabel,
      title: nextPhase?.title || locale.page.roadmap?.nextLabel || guideUi.phaseUnknown,
      body: locale.publicAnswers?.languages || locale.publicAnswers?.milestones || locale.page.guide.body,
    },
  ];

  function stopMicMeter() {
    meterCleanupRef.current?.();
    meterCleanupRef.current = null;
    setMicLevel(0);
  }

  function releaseMicrophone() {
    stopMediaStream(mediaStreamRef.current);
    mediaStreamRef.current = null;
    stopMicMeter();
  }

  function revokePlaybackUrl() {
    if (!playbackUrlRef.current || typeof URL === "undefined") return;
    URL.revokeObjectURL(playbackUrlRef.current);
    playbackUrlRef.current = "";
  }

  function resetPlaybackGraph() {
    const audioElement = audioElementRef.current;
    if (audioElement) {
      audioElement.pause();
      audioElement.removeAttribute("src");
      audioElement.load();
    }
    revokePlaybackUrl();
    setPlaybackLevel(0);

    if (playbackMediaSourceRef.current) {
      try {
        playbackMediaSourceRef.current.disconnect();
      } catch (_error) {}
    }

    if (playbackGainRef.current) {
      try {
        playbackGainRef.current.disconnect();
      } catch (_error) {}
    }

    playbackMediaSourceRef.current = null;
    playbackGainRef.current = null;

    if (playbackAudioContextRef.current) {
      playbackAudioContextRef.current.close().catch(() => {});
    }
    playbackAudioContextRef.current = null;
  }

  function stopSpeakingMotion() {
    if (speakingMotionTimerRef.current) {
      window.clearInterval(speakingMotionTimerRef.current);
      speakingMotionTimerRef.current = null;
    }
    setMouthFrame(0);
    setPlaybackLevel(0);
  }

  function startSpeakingMotion() {
    stopSpeakingMotion();
    speakingMotionTimerRef.current = window.setInterval(() => {
      setMouthFrame((current) => {
        const next = (current + 1) % mouthShapes.speaking.length;
        setPlaybackLevel(Number((0.18 + (next / Math.max(mouthShapes.speaking.length, 1)) * 0.18).toFixed(3)));
        return next;
      });
    }, prefersReducedMotion ? 180 : 110);
  }

  function hardStopListening(options = {}) {
    submitTranscriptOnEndRef.current = false;

    if (recognitionRef.current) {
      recognitionRef.current.onresult = null;
      recognitionRef.current.onerror = null;
      recognitionRef.current.onend = null;
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }

    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onerror = null;
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }

    recordedChunksRef.current = [];
    releaseMicrophone();
    setIsListening(false);
    setCaptureMode("ready");
    setIsTranscribing(false);
    if (!options.keepTranscript) {
      interimTranscriptRef.current = "";
      setInterimTranscript("");
    }
  }

  async function primeAudioPlayback() {
    if (typeof window === "undefined") {
      throw new Error(voiceCopy.unsupportedAudio);
    }

    const audioElement = audioElementRef.current;
    if (!audioElement) {
      throw new Error(voiceCopy.unsupportedAudio);
    }

    audioElement.volume = 1;

    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (AudioContextCtor && !playbackAudioContextRef.current) {
      const audioContext = new AudioContextCtor();
      const mediaSource = audioContext.createMediaElementSource(audioElement);
      const gainNode = audioContext.createGain();

      gainNode.gain.value = PLAYBACK_GAIN_BOOST;
      mediaSource.connect(gainNode);
      gainNode.connect(audioContext.destination);

      playbackAudioContextRef.current = audioContext;
      playbackMediaSourceRef.current = mediaSource;
      playbackGainRef.current = gainNode;
    }

    if (playbackAudioContextRef.current?.state === "suspended") {
      await playbackAudioContextRef.current.resume();
    }

    if (playbackPrimedRef.current) return;

    const silentUrl = URL.createObjectURL(createSilentWavBlob());
    const previousMuted = audioElement.muted;

    try {
      audioElement.muted = true;
      audioElement.src = silentUrl;
      audioElement.load();
      await audioElement.play();
      audioElement.pause();
      audioElement.currentTime = 0;
      playbackPrimedRef.current = true;
    } catch (_error) {
      throw new Error(voiceCopy.audioInteractionRequired);
    } finally {
      audioElement.pause();
      audioElement.removeAttribute("src");
      audioElement.load();
      audioElement.muted = previousMuted;
      URL.revokeObjectURL(silentUrl);
    }
  }

  function stopSpeechPlayback(options = {}) {
    const audioElement = audioElementRef.current;
    if (audioElement) {
      audioElement.pause();
      try {
        audioElement.currentTime = 0;
      } catch (_error) {}
      audioElement.removeAttribute("src");
      audioElement.load();
    }
    revokePlaybackUrl();
    stopSpeakingMotion();
    setIsSpeaking(false);
    setSpeakingMessageId("");
    if (!options.preserveNotice) setVoiceNotice(voiceCopy.idleHint);
  }

  async function askGuide(nextQuestion, options = {}) {
    const trimmedQuestion = String(nextQuestion || "").trim();
    if (!trimmedQuestion || loading) return;

    const conversationHistory = messages.slice(-6).map((message) => ({ role: message.role, text: message.text }));
    const userMessage = createMessage("user", trimmedQuestion);

    stopSpeechPlayback({ preserveNotice: true });
    setMessages((current) => [...current, userMessage]);
    setQuestion("");
    setInterimTranscript("");
    interimTranscriptRef.current = "";
    setVoiceError("");
    setLoading(true);
    setVoiceNotice(voiceCopy.thinkingHint);

    try {
      const response = await fetch(`${config.API_BASE_URL}/api/public/roadmap-guide/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: trimmedQuestion, language, history: conversationHistory }),
      });
      const payload = await response.json();
      const answer = String(payload?.answer || locale.guardrails.error).trim();
      const guideMessage = createMessage("guide", answer);

      setMessages((current) => [...current, guideMessage]);
      if (answer) {
        window.setTimeout(() => playVoiceReply(guideMessage), 80);
      } else {
        setVoiceNotice(voiceCopy.idleHint);
      }
    } catch (_error) {
      setMessages((current) => [...current, createMessage("guide", locale.guardrails.error)]);
      setVoiceError(voiceCopy.voiceError);
      setVoiceNotice(voiceCopy.idleHint);
    } finally {
      setLoading(false);
    }
  }

  async function transcribeCapturedAudio(blob) {
    const mimeType = blob?.type || pickRecordingMimeType();
    const formData = new FormData();
    formData.append("audio", blob, `${GUIDE_NAME}${fileExtensionFromMimeType(mimeType)}`);
    formData.append("language", language);

    const response = await fetch(`${config.API_BASE_URL}/api/public/roadmap-guide/transcribe`, {
      method: "POST",
      body: formData,
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload?.message || voiceCopy.transcribeError);
    }
    return String(payload?.transcript || "").trim();
  }

  async function fetchVoiceReplyBlob(message) {
    const response = await fetch(`${config.API_BASE_URL}/api/public/roadmap-guide/speech`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: message.text, language }),
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload?.message || voiceCopy.unsupportedAudio);
    }

    return response.blob();
  }

  async function playVoiceReply(message) {
    if (typeof window === "undefined" || !message?.text) {
      setVoiceError(voiceCopy.unsupportedAudio);
      return;
    }

    const audioElement = audioElementRef.current;
    if (!audioElement) {
      setVoiceError(voiceCopy.unsupportedAudio);
      return;
    }

    stopSpeechPlayback({ preserveNotice: true });
    setSpeakingMessageId(message.id);
    setVoiceError("");
    setVoiceNotice(voiceCopy.renderingAudio);

    try {
      const audioBlob = await fetchVoiceReplyBlob(message);
      if (!audioBlob?.size) {
        throw new Error(voiceCopy.unsupportedAudio);
      }

      revokePlaybackUrl();
      playbackUrlRef.current = URL.createObjectURL(audioBlob);
      audioElement.volume = 1;
      if (playbackGainRef.current) {
        playbackGainRef.current.gain.value = PLAYBACK_GAIN_BOOST;
      }
      audioElement.src = playbackUrlRef.current;
      audioElement.load();
      await waitForAudioReady(audioElement);
      const playResult = audioElement.play();
      if (playResult?.then) await playResult;
      await waitForPlaybackStart(audioElement);
    } catch (error) {
      stopSpeechPlayback({ preserveNotice: true });
      const nextMessage = error?.name === "NotAllowedError"
        ? voiceCopy.audioInteractionRequired
        : String(error?.message || "").startsWith("audio-")
          ? voiceCopy.unsupportedAudio
          : error?.message || voiceCopy.unsupportedAudio;
      setVoiceError(nextMessage);
      setVoiceNotice(nextMessage);
    }
  }

  function startLiveRecognition(stream) {
    const SpeechRecognitionCtor = getSpeechRecognitionCtor();
    if (!SpeechRecognitionCtor) return false;
    const recognition = new SpeechRecognitionCtor();
    recognitionRef.current = recognition;
    submitTranscriptOnEndRef.current = true;
    setCaptureMode("live-recognition");
    recognition.lang = speechLocaleMap[language] || language;
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      interimTranscriptRef.current = "";
      setInterimTranscript("");
      setVoiceNotice(voiceCopy.listeningHint);
    };
    recognition.onresult = (event) => {
      let captured = "";
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const transcript = String(event.results[index]?.[0]?.transcript || "").trim();
        if (transcript) captured = transcript;
      }
      if (captured) {
        interimTranscriptRef.current = captured;
        setInterimTranscript(captured);
      }
    };
    recognition.onerror = (event) => {
      recognitionRef.current = null;
      releaseMicrophone();
      setIsListening(false);
      setCaptureMode("ready");
      if (event.error === "not-allowed" || event.error === "service-not-allowed") {
        setMicPermissionState("denied");
        setVoiceError(voiceCopy.permissionDenied);
        setVoiceNotice(voiceCopy.permissionDenied);
      } else if (event.error === "no-speech") {
        setVoiceError(voiceCopy.noSpeech);
        setVoiceNotice(voiceCopy.noSpeech);
      } else {
        setVoiceError(voiceCopy.voiceError);
        setVoiceNotice(voiceCopy.voiceError);
      }
    };
    recognition.onend = () => {
      recognitionRef.current = null;
      releaseMicrophone();
      setIsListening(false);
      setCaptureMode("ready");
      const transcript = String(interimTranscriptRef.current || "").trim();
      if (transcript && submitTranscriptOnEndRef.current) {
        setVoiceNotice(voiceCopy.thinkingHint);
        askGuide(transcript, { fromVoice: true });
        return;
      }
      setVoiceNotice(voiceCopy.idleHint);
    };
    recognition.start();
    return true;
  }

  function startRecorderFallback(stream) {
    if (typeof window === "undefined" || typeof window.MediaRecorder === "undefined") {
      return false;
    }

    try {
      const recorder = new window.MediaRecorder(stream, { mimeType: pickRecordingMimeType() });
      mediaRecorderRef.current = recorder;
      submitTranscriptOnEndRef.current = true;
      recordedChunksRef.current = [];
      setCaptureMode("recorded-fallback");

      recorder.onstart = () => {
        setIsListening(true);
        interimTranscriptRef.current = "";
        setInterimTranscript("");
        setVoiceNotice(voiceCopy.recordingHint);
      };
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };
      recorder.onerror = () => {
        mediaRecorderRef.current = null;
        recordedChunksRef.current = [];
        releaseMicrophone();
        setIsListening(false);
        setCaptureMode("ready");
        setVoiceError(voiceCopy.voiceError);
        setVoiceNotice(voiceCopy.voiceError);
      };
      recorder.onstop = async () => {
        const blob = recordedChunksRef.current.length ? new Blob(recordedChunksRef.current, { type: recorder.mimeType || pickRecordingMimeType() }) : null;
        mediaRecorderRef.current = null;
        recordedChunksRef.current = [];
        releaseMicrophone();
        setIsListening(false);
        setCaptureMode("ready");

        if (!submitTranscriptOnEndRef.current) return;
        if (!blob || blob.size === 0) {
          setVoiceError(voiceCopy.noSpeech);
          setVoiceNotice(voiceCopy.noSpeech);
          return;
        }

        setIsTranscribing(true);
        setVoiceError("");
        setVoiceNotice(voiceCopy.transcribingHint);
        try {
          const transcript = await transcribeCapturedAudio(blob);
          if (!transcript) {
            setVoiceError(voiceCopy.noSpeech);
            setVoiceNotice(voiceCopy.noSpeech);
            return;
          }
          interimTranscriptRef.current = transcript;
          setInterimTranscript(transcript);
          await askGuide(transcript, { fromVoice: true });
        } catch (error) {
          setVoiceError(error?.message || voiceCopy.transcribeError);
          setVoiceNotice(error?.message || voiceCopy.transcribeError);
        } finally {
          setIsTranscribing(false);
        }
      };
      recorder.start();
      return true;
    } catch (_error) {
      return false;
    }
  }

  async function startListening() {
    if (loading || isListening || isTranscribing) return;
    setHasRequestedMicrophone(true);

    try {
      await primeAudioPlayback();
    } catch (_error) {}

    stopSpeechPlayback({ preserveNotice: true });
    setVoiceError("");
    setVoiceNotice(micPermissionState === "denied" ? voiceCopy.permissionDenied : voiceCopy.listeningHint);

    try {
      const stream = await requestAudioStream({ audio: true });
      mediaStreamRef.current = stream;
      setMicPermissionState("granted");
      stopMicMeter();
      meterCleanupRef.current = createAudioLevelMonitor(stream, setMicLevel);

      if (startLiveRecognition(stream)) return;
      if (startRecorderFallback(stream)) return;

      releaseMicrophone();
      setMicPermissionState("unsupported");
      setVoiceError(voiceCopy.unsupportedMic);
      setVoiceNotice(voiceCopy.unsupportedMic);
    } catch (error) {
      releaseMicrophone();
      if (error?.name === "NotAllowedError" || error?.name === "SecurityError") {
        setMicPermissionState("denied");
        setVoiceError(voiceCopy.permissionDenied);
        setVoiceNotice(voiceCopy.permissionDenied);
        return;
      }
      setVoiceError(voiceCopy.micMissing);
      setVoiceNotice(voiceCopy.micMissing);
    }
  }

  function handleToggleListening() {
    if (isListening) {
      stopAndSubmitCapture();
      return;
    }
    startListening();
  }

  async function handleAskFromUi(nextQuestion) {
    const trimmedQuestion = String(nextQuestion || "").trim();
    if (!trimmedQuestion) return;

    try {
      await primeAudioPlayback();
    } catch (_error) {}

    askGuide(trimmedQuestion);
  }

  function stopAndSubmitCapture() {
    submitTranscriptOnEndRef.current = true;
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
  }

  useEffect(() => {
    interimTranscriptRef.current = interimTranscript;
  }, [interimTranscript]);

  useEffect(() => {
    const audioElement = audioElementRef.current;
    if (!audioElement) return undefined;

    const clearPlaybackElement = () => {
      revokePlaybackUrl();
      audioElement.removeAttribute("src");
      audioElement.load();
      setPlaybackLevel(0);
    };

    const handlePlaying = () => {
      startSpeakingMotion();
      setIsSpeaking(true);
      setVoiceError("");
      setVoiceNotice(voiceCopy.speakingHint);
    };

    const handlePause = () => {
      if (audioElement.ended) return;
      stopSpeakingMotion();
      setIsSpeaking(false);
    };

    const handleEnded = () => {
      stopSpeakingMotion();
      setIsSpeaking(false);
      setSpeakingMessageId("");
      clearPlaybackElement();
      setVoiceNotice(voiceCopy.idleHint);
    };

    const handleError = () => {
      stopSpeakingMotion();
      setIsSpeaking(false);
      setSpeakingMessageId("");
      clearPlaybackElement();
      setVoiceError(voiceCopy.unsupportedAudio);
      setVoiceNotice(voiceCopy.unsupportedAudio);
    };

    audioElement.addEventListener("playing", handlePlaying);
    audioElement.addEventListener("pause", handlePause);
    audioElement.addEventListener("ended", handleEnded);
    audioElement.addEventListener("error", handleError);

    return () => {
      audioElement.removeEventListener("playing", handlePlaying);
      audioElement.removeEventListener("pause", handlePause);
      audioElement.removeEventListener("ended", handleEnded);
      audioElement.removeEventListener("error", handleError);
    };
  }, [voiceCopy.idleHint, voiceCopy.speakingHint, voiceCopy.unsupportedAudio]);

  useEffect(() => {
    hardStopListening({ keepTranscript: false });
    stopSpeechPlayback({ preserveNotice: true });
    resetPlaybackGraph();
    playbackPrimedRef.current = false;
    setMessages([]);
    setQuestion("");
    setInterimTranscript("");
    setVoiceError("");
    setSpeakingMessageId("");
    setCaptureMode("ready");
    setHasRequestedMicrophone(false);
    setVoiceNotice(voiceCopy.idleHint);
    if (!speechRecognitionSupported) setMicPermissionState("prompt");
  }, [language, speechRecognitionSupported, voiceCopy.idleHint]);

  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.permissions?.query) {
      return undefined;
    }

    let active = true;
    let permissionStatus = null;

    navigator.permissions.query({ name: "microphone" }).then((status) => {
      if (!active) return;
      permissionStatus = status;
      setMicPermissionState(status.state);
      status.onchange = () => active && setMicPermissionState(status.state);
    }).catch(() => active && setMicPermissionState("prompt"));

    return () => {
      active = false;
      if (permissionStatus) permissionStatus.onchange = null;
    };
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) {
      setBlinkClosed(false);
      return undefined;
    }

    let blinkTimer = null;
    let openTimer = null;
    let active = true;

    const scheduleBlink = () => {
      blinkTimer = window.setTimeout(() => {
        if (!active) return;
        setBlinkClosed(true);
        openTimer = window.setTimeout(() => {
          if (!active) return;
          setBlinkClosed(false);
          scheduleBlink();
        }, 140);
      }, 3200 + Math.round(Math.random() * 1600));
    };

    scheduleBlink();
    return () => {
      active = false;
      if (blinkTimer) window.clearTimeout(blinkTimer);
      if (openTimer) window.clearTimeout(openTimer);
    };
  }, [prefersReducedMotion]);

  useEffect(() => () => {
    hardStopListening({ keepTranscript: false });
    stopSpeechPlayback({ preserveNotice: true });
    resetPlaybackGraph();
  }, []);

  return (
    <section className="public-roadmap-guide" dir={languageMeta.dir} lang={language} aria-label={locale.page.guide.title}>
      <div className="public-roadmap-guide__identity public-page-card">
        <div className="public-roadmap-guide__topline">
          <span className="public-page-overline">{locale.page.guide.eyebrow}</span>
        </div>

        <div className="public-roadmap-guide__identity-copy">
          <h2 className="public-page-subtitle">{guideUi.title}</h2>
          <p className="public-page-body">{locale.page.guide.body}</p>
        </div>

        <div className="public-roadmap-guide__status-strip" role="list">
          <article className="public-roadmap-guide__status-card" role="listitem">
            <span>{voiceCopy.status}</span>
            <strong>{guideDisplayName}</strong>
            <small>{voiceMode}</small>
          </article>
          <article className="public-roadmap-guide__status-card" role="listitem">
            <span>{voiceCopy.language}</span>
            <strong>{languageMeta.nativeLabel}</strong>
            <small>{currentPhase?.title || guideUi.phaseUnknown}</small>
          </article>
        </div>

        <div className={`public-roadmap-guide__console is-${avatarState}`} style={{ "--public-guide-level": `${Math.max(0.04, Math.max(micLevel, playbackLevel))}` }}>
          <div className="public-roadmap-guide__console-stage">
            <PublicGuideTalkingHead
              avatarState={avatarState}
              mouthFrame={mouthFrame}
              micLevel={micLevel}
              playbackLevel={playbackLevel}
              blinkClosed={blinkClosed}
              locale={locale}
              voiceCopy={voiceCopy}
              isListening={isListening}
              onToggleListening={handleToggleListening}
            />

            <div className="public-roadmap-guide__voice-panel">
              <div className="public-roadmap-guide__voice-feedback">
                <div className="public-roadmap-guide__notice">
                  <strong>{guideUi.scopeLabel}</strong>
                  <div className="public-roadmap-guide__notice-copy">
                    {(guideIntroParagraphs.slice(0, 2).length ? guideIntroParagraphs.slice(0, 2) : [locale.page.guide.body]).map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </div>
                  <div className="public-roadmap-guide__signal-grid" role="list">
                    <article className="public-roadmap-guide__signal-card" role="listitem">
                      <span>{guideUi.currentPhaseLabel}</span>
                      <strong>{currentPhase?.title || guideUi.phaseUnknown}</strong>
                    </article>
                    <article className="public-roadmap-guide__signal-card" role="listitem">
                      <span>{locale.page.roadmap.nextLabel || "Next"}</span>
                      <strong>{nextPhase?.title || guideUi.phaseUnknown}</strong>
                    </article>
                    <article className="public-roadmap-guide__signal-card" role="listitem">
                      <span>{guideUi.voiceLabel}</span>
                      <strong>{voiceMode}</strong>
                    </article>
                  </div>
                </div>

                {interimTranscript ? (
                  <div className="public-roadmap-guide__transcript">
                    <strong>{voiceCopy.transcript}</strong>
                    <p>{interimTranscript}</p>
                  </div>
                ) : null}
              </div>

              <audio ref={audioElementRef} hidden preload="auto" playsInline />
            </div>
          </div>

          <form
            className="public-roadmap-guide__composer"
            onSubmit={(event) => {
              event.preventDefault();
              handleAskFromUi(question);
            }}
          >
            <div className="public-roadmap-guide__composer-head">
              <div className="public-roadmap-guide__composer-copy">
                <strong>{guideUi.askLabel}</strong>
                <span>{guideUi.askHint}</span>
              </div>
              <button
                type="submit"
                className="public-roadmap-guide__composer-submit"
                disabled={loading || isTranscribing || !String(question || "").trim()}
              >
                {locale.page.guide.sendLabel || guideUi.submitLabel}
              </button>
            </div>
            <CFormTextarea
              rows={2}
              value={question}
              placeholder={guideUi.askPlaceholder || locale.page.guide.inputPlaceholder}
              enterKeyHint="send"
              onChange={(event) => setQuestion(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== "Enter" || event.nativeEvent?.isComposing) return;
                event.preventDefault();
                handleAskFromUi(question);
              }}
              disabled={loading || isTranscribing}
            />
            <div className="public-roadmap-guide__prompt-strip" role="list" aria-label={guideUi.quickPromptsLabel}>
              {exampleQuestions.map((entry) => (
                <button
                  key={entry}
                  type="button"
                  className="public-roadmap-guide__prompt-chip"
                  onClick={() => handleAskFromUi(entry)}
                  disabled={loading || isTranscribing}
                  role="listitem"
                >
                  {entry}
                </button>
              ))}
            </div>
          </form>

          <div className="public-roadmap-guide__responses">
            {messages.length || loading || isTranscribing ? (
              <>
                <div className="public-roadmap-guide__responses-head">
                  <strong>{guideUi.responseTitle}</strong>
                  <p>{guideUi.responseLead}</p>
                </div>
                <div className="public-roadmap-guide__messages" aria-live="polite">
                  {messages.map((message) => (
                    <article key={message.id} className={`public-roadmap-guide__message public-roadmap-guide__message--${message.role} ${message.id === speakingMessageId ? "is-speaking" : ""}`.trim()}>
                      <div className="public-roadmap-guide__message-head">
                        <span className="public-roadmap-guide__message-label">{message.role === "guide" ? guideUi.answerLabel : guideUi.questionLabel}</span>
                      </div>
                      <div className="public-roadmap-guide__message-body">
                        {normaliseMessageBlocks(message.text).map((block, index) => block.type === "ordered" ? (
                          <ol key={`${message.id}-ordered-${index}`} className="public-roadmap-guide__message-list public-roadmap-guide__message-list--ordered">{block.items.map((item, itemIndex) => <li key={`${message.id}-ordered-${itemIndex}`}>{item}</li>)}</ol>
                        ) : block.type === "unordered" ? (
                          <ul key={`${message.id}-unordered-${index}`} className="public-roadmap-guide__message-list">{block.items.map((item, itemIndex) => <li key={`${message.id}-unordered-${itemIndex}`}>{item}</li>)}</ul>
                        ) : (
                          <p key={`${message.id}-paragraph-${index}`}>{block.text}</p>
                        ))}
                      </div>
                    </article>
                  ))}

                  {loading || isTranscribing ? (
                    <article className="public-roadmap-guide__message public-roadmap-guide__message--guide is-thinking">
                      <div className="public-roadmap-guide__message-head">
                        <span className="public-roadmap-guide__message-label">{guideUi.answerLabel}</span>
                      </div>
                      <div className="public-roadmap-guide__message-body">
                        <p>{isTranscribing ? voiceCopy.transcribingHint : locale.page.guide.thinkingLabel}</p>
                      </div>
                    </article>
                  ) : null}
                </div>
              </>
            ) : (
              <div className="public-roadmap-guide__curated-grid">
                {curatedCards.map((card) => (
                  <article key={card.key} className={`public-roadmap-guide__curated-card public-roadmap-guide__curated-card--${card.key}`}>
                    <span>{card.eyebrow}</span>
                    <strong>{card.title}</strong>
                    <p>{card.body}</p>
                  </article>
                ))}

                <article className="public-roadmap-guide__curated-card public-roadmap-guide__curated-card--legend">
                  <span>{guideUi.statusLegendLabel}</span>
                  <strong>{locale.page.guide.guardrailTitle || guideUi.statusLegendLabel}</strong>
                  <div className="public-roadmap-guide__legend-list">
                    {statusLegend.map((item) => (
                      <div key={item.key} className="public-roadmap-guide__legend-item">
                        <div className="public-roadmap-guide__legend-top">
                          <span className={`public-roadmap-status public-roadmap-status--${item.key}`}>{item.label}</span>
                        </div>
                        <p>{item.body}</p>
                      </div>
                    ))}
                  </div>
                </article>
              </div>
            )}

            {hasRequestedMicrophone && !speechRecognitionSupported && typeof window !== "undefined" && typeof window.MediaRecorder === "undefined" && !voiceError ? (
              <div className="public-roadmap-guide__fallback">
                <strong>{voiceCopy.status}</strong>
                <p>{voiceCopy.unsupportedMic}</p>
              </div>
            ) : null}

            {hasRequestedMicrophone && micPermissionState === "prompt" && !isListening ? (
              <div className="public-roadmap-guide__fallback">
                <strong>{voiceCopy.status}</strong>
                <p>{voiceCopy.permissionPrompt}</p>
              </div>
            ) : null}

            {voiceError ? (
              <div className="public-roadmap-guide__fallback">
                <strong>{voiceCopy.status}</strong>
                <p>{voiceError}</p>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
